// Presentation Director (docs/07 §8.3): server events → choreographed beats. It owns the
// *presented* view, committing each event's effect when its animation lands, then reconciles
// with the authoritative view at the end of every batch.
import type { AvailableActions, CardId, Color, DefId, GameEvent, LocationId, LoggedEvent, PlayerView, SeatId, SourceRef } from '@bcm/rules';
import type { BoardScene } from '../scene/BoardScene';
import { applyEventToView } from '../game/reducer';
import { C, COLOR_OF_SCHOOL, SEAL_COLORS, closingHour, defName, isSpell, lastRound, locationName, logDefs, logLine, resolutionInForce, resolutionRoom, roundHour, shortLocation, spell } from '../game/content';
import { ICON_PATHS, SCHOOL_ICON } from '../game/icons';
import { props } from '../scene/assets';
import { audio } from '../audio/sfx';
import { STACK_DROP, STACK_STAGGER, anchor, banner, bump, cardStack, float, fly, fogClear, plate, rectOf, showcase, type Point } from './fx';
import { useGame } from '../state/store';

interface Batch {
  events: LoggedEvent[];
  view: PlayerView;
  actions: AvailableActions | null;
  /** A game that starts without a draft: play the table setup before anything else. */
  setup?: boolean;
}

/**
 * The table before anything is dealt. The engine deals hands, the Arcanium and the City Problems
 * before the draft; the client holds that back so the draft happens over an empty table and the
 * deal is played out afterwards (Director.setupSequence).
 */
function undealt(view: PlayerView): PlayerView {
  const v = structuredClone(view);
  v.spellDeckCount += v.arcanium.filter(Boolean).length + v.seats.reduce((n, s) => n + s.spellHandCount, 0);
  v.actionDeckCount += v.seats.reduce((n, s) => n + s.actionHandCount, 0);
  v.arcanium = v.arcanium.map(() => null);
  v.problems = [];
  for (const s of v.seats) {
    s.spellHandCount = 0;
    s.actionHandCount = 0;
    if (s.spellHand) s.spellHand = [];
    if (s.actionHand) s.actionHand = [];
  }
  return v;
}

const wait = (s: number) => new Promise<void>((r) => setTimeout(r, s * 1000));

/** Real seconds a card you didn't play (a rival's, the Ministry's) rests at the centre, to be read. */
const READ_HOLD = 2;
/** Banner pace for a rule that changes for everyone (P-17): its text rests ~2 s, whatever the speed. */
const RULE_BANNER = 0.42;
/** Real seconds "Spell Activated" cards rest to be read: 2 s, and 1 s more for each further card. */
const SPELL_REST = 2;
const SPELL_REST_EACH = 1;

/**
 * Events that start a new moment: a player's own move, a choice, or the turn and round turning
 * over. Spell effects between two of these go off together (see `spellMoments`).
 */
const CAUSES = new Set<GameEvent['t']>([
  'AssistantPlaced',
  'SpellCast',
  'ActionCardPlayed',
  'SpellPurchased',
  'ProblemClaimed',
  'AbilityUsed',
  'DecisionRequested',
  'DecisionResolved',
  'TurnStarted',
  'TurnEnded',
  'RoundStarted',
  'GameEnded',
]);

type SpellEffect = Extract<GameEvent, { t: 'AbilityTriggered' | 'AbilityUsed' }> & { source: { kind: 'card'; card: CardId; def: DefId } };

/** A cast spell's triggered or activated ability going off. */
const isSpellEffect = (e: GameEvent): e is SpellEffect =>
  (e.t === 'AbilityTriggered' || e.t === 'AbilityUsed') && e.source.kind === 'card' && isSpell(e.source.def);

const escapeHtml = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
const GLYPH: Record<string, string> = { R: 'red', Y: 'yellow', B: 'blue', G: 'green', MANA: 'mana', VP: 'vp' };

/** A spell as a row of a rival's Spells list (school, name, effect), for the FX layer. */
function spellRowHtml(def: DefId): string {
  const sp = spell(def);
  const text = escapeHtml(sp.text).replace(/\{([A-Z]+)\}/g, (m, k: string) => (GLYPH[k] ? `<span class="glyph glyph-${GLYPH[k]}"></span>` : m));
  const icon = `<svg class="icon" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path d="${ICON_PATHS[SCHOOL_ICON[sp.school]]}" fill-rule="evenodd" fill="currentColor" /></svg>`;
  return `<div class="dos-row s-${sp.school}">${icon}<span class="dos-row-name">${escapeHtml(sp.name)}</span><span class="dos-row-text">${text}</span></div>`;
}

const PERMIT_HEX: Record<Color, string> = { red: '#c8443a', yellow: '#e0b43c', blue: '#3f7fe0', green: '#3fa86a' };
const glyphHtml = (c: Color) => `<span class="glyph glyph-${c}"></span>`;

class Director {
  private scene: BoardScene | null = null;
  private queue: Batch[] = [];
  private running = false;
  private generation = 0;
  private optimistic: { assistant: string; location: LocationId; done: Promise<void> } | null = null;
  /** The dealt table, held back until the opening setup plays (see `undealt`). */
  private dealt: PlayerView | null = null;
  /** The room of the last placement (a Resolution's beat with no room of its own lands there). */
  private lastPlaced: LocationId | null = null;
  /** Spell effects already shown as "Spell Activated" cards (their own beat only commits). */
  private shownSpells = new WeakSet<GameEvent>();
  /** The last "Spell Activated" cards tucking back in: the next plate under a panel waits for them. */
  private stacks: Promise<void> = Promise.resolve();

  /** What to present for an authoritative view: during the draft, the table is not dealt yet. */
  private present(view: PlayerView): PlayerView {
    if (view.phase === 'draft' && view.round === 0) {
      this.dealt = structuredClone(view);
      return undealt(view);
    }
    return structuredClone(view);
  }

  bindScene(scene: BoardScene | null): void {
    this.scene = scene;
    const p = useGame.getState().presented;
    if (scene && p) {
      scene.setSeats(p.seats.map((s) => s.name));
      scene.sync(p);
      scene.setRound(p.round, p.phase === 'ended');
    }
  }

  reset(): void {
    this.generation++;
    this.queue = [];
    this.running = false;
    this.optimistic = null;
    this.dealt = null;
    this.stacks = Promise.resolve();
  }

  /** Full resync (join, reconnect, undo): no animation, jump to the authoritative view. */
  snap(view: PlayerView, actions: AvailableActions | null, events: LoggedEvent[]): void {
    this.generation++;
    this.queue = [];
    this.running = false;
    this.optimistic = null;
    const s = useGame.getState();
    const names = (seat: number) => view.seats[seat]?.name ?? `Seat ${seat + 1}`;
    const lines = events
      .map((le) => ({ seq: le.seq, text: logLine(le.e, names), seat: 'seat' in le.e ? (le.e.seat as number) : undefined, defs: logDefs(le.e) }))
      .filter((l): l is { seq: number; text: string; seat: number | undefined; defs: string[] } => l.text !== null);
    const log = events.length && events[0]!.seq <= 1 ? lines : [...s.log, ...lines];
    this.dealt = null;
    // A game that started without a draft and hasn't been played yet still gets its opening deal.
    const fresh =
      view.phase === 'action' && view.round === 1 && events.length > 0 && events[0]!.seq <= 1 && !events.some((le) => le.e.t === 'AssistantPlaced' || le.e.t === 'TurnEnded');
    if (fresh) this.dealt = structuredClone(view);
    const presented = fresh ? undealt(view) : this.present(view);
    useGame.setState({ presented, presentedActions: actions, busy: false, log: log.slice(-200) });
    if (this.scene) {
      this.scene.setSeats(view.seats.map((x) => x.name));
      this.scene.sync(presented);
      this.scene.setRound(view.round, view.phase === 'ended');
    }
    if (fresh) this.enqueue([], view, actions, true);
  }

  enqueue(events: LoggedEvent[], view: PlayerView, actions: AvailableActions | null, setup = false): void {
    this.queue.push({ events, view, actions, setup });
    if (!this.running) void this.run();
  }

  /** Start the local player's hop immediately on click, before the server confirms (docs/07 §8.6). */
  optimisticPlace(seat: SeatId, location: LocationId): void {
    const scene = this.scene;
    const p = useGame.getState().presented;
    if (!scene || !p || this.running) return;
    const st = p.seats[seat];
    if (!st || !p.turn) return;
    const next = (!p.turn.basePlaced && st.assistants.find((a) => !a.temp && !a.placed)) || st.assistants.find((a) => a.temp && !a.placed);
    if (!next) return;
    const preview = structuredClone(p);
    applyEventToView(preview, { t: 'AssistantPlaced', seat, assistant: next.id, location, slot: preview.board[location].occupants.length, temp: next.temp });
    const to = scene.stationPosition(preview, seat, next.id);
    this.optimistic = { assistant: next.id, location, done: scene.hop(next.id, seat, to, 1) };
  }

  private async run(): Promise<void> {
    this.running = true;
    const gen = this.generation;
    useGame.setState({ busy: true });
    while (this.queue.length && gen === this.generation) {
      const batch = this.queue.shift()!;
      if (batch.setup && this.dealt) {
        await this.setupSequence(1);
        if (gen !== this.generation) return;
        const s = useGame.getState();
        await banner(`Round ${batch.view.round}`, roundHour(batch.view.round), 1, 'banner-round');
        if (batch.view.activeSeat === s.you?.seat) await banner('Your turn', 'Send an Assistant to a department', 1.4, 'banner-turn');
      }
      const moments = this.spellMoments(batch.events);
      for (const le of batch.events) {
        if (gen !== this.generation) return;
        const moment = moments.get(le);
        if (moment) await this.spellsActivated(moment, this.speedFor(le.e));
        if (gen !== this.generation) return;
        await this.play(le, this.speedFor(le.e));
      }
      if (gen !== this.generation) return;
      const presented = this.present(batch.view);
      useGame.setState({ presented, presentedActions: batch.actions });
      this.scene?.sync(presented);
    }
    if (gen === this.generation) {
      this.running = false;
      useGame.setState({ busy: false });
    }
  }

  private speedFor(e: GameEvent): number {
    if (document.hidden) return 40;
    const s = useGame.getState();
    // Turn handoffs keep one readable pace: they are text the player should have time to read, so
    // neither the opponent-speed setting nor the queue catch-up applies.
    if (e.t === 'TurnEnded' || e.t === 'TurnStarted') return s.settings.reducedMotion ? 1.2 : 1;
    const mine = 'seat' in e && e.seat === s.you?.seat;
    let speed = mine ? 1 : s.settings.opponentSpeed;
    if (this.queue.length > 3) speed *= 3;
    if (s.settings.reducedMotion) speed *= 1.5;
    return speed;
  }

  private commit(e: GameEvent, seq?: number): void {
    const s = useGame.getState();
    if (!s.presented) return;
    const presented = applyEventToView(structuredClone(s.presented), e);
    const name = (seat: number) => presented.seats[seat]?.name ?? `Seat ${seat + 1}`;
    const text = logLine(e, name);
    const log = text && seq !== undefined && seq >= 0 ? [...s.log, { seq, text, seat: 'seat' in e ? (e.seat as number) : undefined, defs: logDefs(e) }].slice(-200) : s.log;
    useGame.setState({ presented, log });
  }

  // ─── Anchors ───────────────────────────────────────────────────────────────

  private isMe(seat: number): boolean {
    return useGame.getState().you?.seat === seat;
  }

  /** A showcased card's rest: your own plays keep their beat, anyone else's is held to be read. */
  private readHold(seat: number): number | undefined {
    return this.isMe(seat) ? undefined : READ_HOLD;
  }

  /** `hand` is your spells tray; `actions` your Action cards tray (a rival's card holds both). */
  private seatTarget(seat: number, what: 'permits' | 'mana' | 'vp' | 'hand' | 'actions', color?: Color): Point {
    const names = this.isMe(seat)
      ? [what === 'permits' && color ? `desk-permit-${color}` : `desk-${what}`, 'desk-hand', `seat-${seat}-${what}`]
      : [`seat-${seat}-${what === 'actions' ? 'hand' : what}`, `seat-${seat}`];
    return anchor(...names) ?? { x: innerWidth / 2, y: innerHeight - 80 };
  }

  private bumpSeat(seat: number, what: string, color?: Color): void {
    if (this.isMe(seat)) bump(what === 'permits' && color ? `desk-permit-${color}` : `desk-${what}`, `seat-${seat}-${what}`);
    else bump(`seat-${seat}-${what}`);
  }

  private sourcePoint(src: SourceRef, seat: number): Point {
    if (src.kind === 'location' && this.scene) return this.scene.screenPos(this.scene.roomAnchor(src.id));
    if (src.kind === 'resolution' && this.scene) {
      // A Resolution that changes a room speaks from it (Storm Power at Evocation, Morning Rush where
      // the round's first clerk just landed).
      const loc = resolutionRoom(src.def) ?? this.lastPlaced;
      if (loc) return this.scene.screenPos(this.scene.roomAnchor(loc));
    }
    if (src.kind === 'card') return anchor(`card-${src.card}`, `seat-${seat}`) ?? this.center();
    if ((src.kind === 'identity' || src.kind === 'rules') && this.isMe(seat)) return anchor('desk-identity', `seat-${seat}`) ?? this.center();
    return anchor(`seat-${seat}`) ?? this.center();
  }

  /** A player's own spot on screen: your dossier, or the rival's card in the top bar. */
  private playerPoint(seat: number): Point {
    return (this.isMe(seat) ? anchor('desk-identity') : anchor(`seat-${seat}`)) ?? this.center();
  }

  /** Where a card leaves a player's hand: the card itself in your tray, or the rival's card. */
  private handPoint(seat: number, card?: string): Point {
    return (this.isMe(seat) ? anchor(card ? `card-${card}` : 'desk-hand', 'desk-hand') : anchor(`seat-${seat}`)) ?? this.center();
  }

  /** A player's cast spells: your Spells Cast section, or the rival's Spells bar. */
  private spellsPoint(seat: number): Point {
    return (this.isMe(seat) ? anchor('desk-spells', 'desk-identity') : anchor(`seat-${seat}-spells`, `seat-${seat}`)) ?? this.center();
  }

  private problemPoint(problem: string): Point {
    return this.scene ? this.scene.cardScreenPos({ kind: 'problem', problem }) : this.center();
  }

  private center(): Point {
    return { x: innerWidth / 2, y: innerHeight / 2 };
  }

  // ─── Spell Activated ───────────────────────────────────────────────────────

  /**
   * The batch's spell effects grouped into the moments they go off together, keyed by the event
   * that starts each moment: everything one move (or the round's start) sets off, up to the next
   * cause. A spell's own on-cast effect is left out: its cast showcase has just shown the card.
   */
  private spellMoments(events: LoggedEvent[]): Map<LoggedEvent, SpellEffect[]> {
    const moments = new Map<LoggedEvent, SpellEffect[]>();
    let current: SpellEffect[] | null = null;
    let onCast: CardId | null = null;
    for (const le of events) {
      const e = le.e;
      if (CAUSES.has(e.t)) current = null;
      if (e.t === 'SpellCast') onCast = spell(e.def).abilities.some((a) => a.kind === 'onCast') ? e.card : null;
      if (!isSpellEffect(e)) continue;
      if (e.t === 'AbilityTriggered' && e.source.card === onCast) {
        onCast = null;
        continue;
      }
      if (!current) moments.set(le, (current = []));
      current.push(e);
    }
    return moments;
  }

  /**
   * "Spell Activated": the spells of one moment drop out from under their owners' panels, slightly
   * staggered, and rest to be read — 2 s, plus 1 s for each further card, in real time — before
   * their effects play. Your own come out of your panel's right edge.
   */
  private async spellsActivated(effects: SpellEffect[], speed: number): Promise<void> {
    for (const e of effects) this.shownSpells.add(e);
    if (document.hidden) return;
    const bySeat = new Map<SeatId, DefId[]>();
    const seen = new Set<CardId>();
    // Rivals' first, then yours: your stack settles below any of theirs it would cover.
    const ordered = [...effects.filter((e) => !this.isMe(e.seat)), ...effects.filter((e) => this.isMe(e.seat))];
    for (const e of ordered) {
      if (seen.has(e.source.card)) continue; // the same spell twice in one moment (Morning Rush) shows once
      seen.add(e.source.card);
      bySeat.set(e.seat, [...(bySeat.get(e.seat) ?? []), e.source.def]);
    }
    const n = seen.size;
    const landed = ((n - 1) * STACK_STAGGER + STACK_DROP) / speed;
    const until = landed + SPELL_REST + (n - 1) * SPELL_REST_EACH;
    await this.stacks;
    audio.play('reveal', { gain: 0.6 });
    let first = 0;
    const stacks: Promise<void>[] = [];
    for (const [seat, defs] of bySeat) {
      const me = this.isMe(seat);
      // A window under 900 px hides your panel: your cards then come out of the screen's left edge.
      const rect = me ? rectOf('.my-panel') ?? new DOMRect(0, 0, 0, 0) : rectOf(`[data-anchor="seat-${seat}"]`);
      const title = defs.length > 1 ? 'Spells Activated' : 'Spell Activated';
      stacks.push(cardStack(rect, title, defs.map(spellRowHtml), { side: me ? 'right' : 'below', speed, accent: SEAL_COLORS[seat], first, until }));
      defs.forEach((_, i) => window.setTimeout(() => audio.play('card', { gain: me ? 0.8 : 0.5 }), ((first + i) * STACK_STAGGER * 1000) / speed));
      first += defs.length;
    }
    this.stacks = Promise.all(stacks).then(() => undefined);
    // The effects follow as the cards tuck back in.
    await wait(until);
  }

  // ─── Beats ─────────────────────────────────────────────────────────────────

  private async play(le: LoggedEvent, speed: number): Promise<void> {
    const e = le.e;
    const scene = this.scene;
    const commit = () => this.commit(e, le.seq);
    // Hidden tab: no animation at all — just keep the presented state current (docs/06 §5.3).
    if (document.hidden) {
      if (e.t === 'DraftResolved') this.dealt = null; // no opening show in a background tab
      commit();
      if (e.t === 'RoundStarted') scene?.setRound(e.round);
      return;
    }
    switch (e.t) {
      case 'AssistantPlaced': {
        this.lastPlaced = e.location;
        commit();
        const p = useGame.getState().presented!;
        if (this.optimistic && this.optimistic.assistant === e.assistant) {
          await this.optimistic.done;
          this.optimistic = null;
        } else if (scene) {
          if (!this.isMe(e.seat) && useGame.getState().settings.followCamera) scene.follow(e.location);
          audio.play('hop');
          await scene.hop(e.assistant, e.seat, scene.stationPosition(p, e.seat, e.assistant), speed);
          audio.play('place');
        }
        return;
      }
      case 'PermitsGained': {
        const from = this.sourcePoint(e.source, e.seat);
        if (e.source.kind === 'location' && scene) void scene.burst(e.source.id, PERMIT_HEX[(Object.keys(e.permits)[0] as Color) ?? 'red'], speed);
        const flights: Promise<void>[] = [];
        let i = 0;
        for (const [c, n] of Object.entries(e.permits) as [Color, number][]) {
          for (let k = 0; k < Math.min(n, 4); k++, i++) {
            flights.push(fly({ from, to: this.seatTarget(e.seat, 'permits', c), html: glyphHtml(c), className: 'token-permit', delay: i * 0.08, speed }));
          }
        }
        await Promise.all(flights);
        audio.play('permit', { gain: this.isMe(e.seat) ? 1 : 0.5 });
        commit();
        for (const c of Object.keys(e.permits) as Color[]) this.bumpSeat(e.seat, 'permits', c);
        float(this.seatTarget(e.seat, 'permits', Object.keys(e.permits)[0] as Color), `+${Object.values(e.permits).reduce((a, b) => a + (b ?? 0), 0)}`, 'float-permit', speed);
        return;
      }
      case 'ManaGained': {
        const from = this.sourcePoint(e.source, e.seat);
        if (e.source.kind === 'location' && scene) void scene.burst(e.source.id, '#f26bbd', speed);
        const flights = Array.from({ length: Math.min(e.amount, 4) }, (_, i) =>
          fly({ from, to: this.seatTarget(e.seat, 'mana'), html: '<span class="glyph glyph-mana"></span>', className: 'token-mana', delay: i * 0.07, speed }),
        );
        await Promise.all(flights);
        audio.play('mana', { gain: this.isMe(e.seat) ? 1 : 0.5 });
        commit();
        this.bumpSeat(e.seat, 'mana');
        float(this.seatTarget(e.seat, 'mana'), `+${e.amount}`, 'float-mana', speed);
        return;
      }
      case 'ManaSpent':
      case 'PermitsSpent': {
        commit();
        const what = e.t === 'ManaSpent' ? 'mana' : 'permits';
        const amount = e.t === 'ManaSpent' ? e.amount : Object.values(e.permits).reduce((a, b) => a + (b ?? 0), 0);
        float(this.seatTarget(e.seat, what), `−${amount}`, 'float-spend', speed);
        return;
      }
      case 'VPGained': {
        if (e.amount < 0) {
          commit();
          float(this.seatTarget(e.seat, 'vp'), `${e.amount} VP`, 'float-spend', speed);
          return;
        }
        const from =
          e.source.kind === 'problem' ? this.problemPoint(e.source.def) : e.source.kind === 'card' ? this.spellsPoint(e.seat) : this.sourcePoint(e.source, e.seat);
        await fly({ from, to: this.seatTarget(e.seat, 'vp'), html: '<span class="glyph glyph-vp"></span>', className: 'token-vp', speed });
        audio.play('vp', { gain: this.isMe(e.seat) ? 1 : 0.6 });
        commit();
        this.bumpSeat(e.seat, 'vp');
        float(this.seatTarget(e.seat, 'vp'), `+${e.amount} VP`, 'float-vp', speed);
        return;
      }
      case 'RankPromoted': {
        commit();
        if (this.isMe(e.seat)) {
          audio.play('reveal');
          await banner('Promoted!', e.title, speed * 1.2, 'banner-rank');
        }
        return;
      }
      case 'CardDrawn': {
        const from = scene ? scene.cardScreenPos({ kind: e.deck === 'action' ? 'actionDeck' : 'spellDeck' }) : this.center();
        audio.play('card', { gain: this.isMe(e.seat) ? 1 : 0.5 });
        if (scene && e.deck === 'action') {
          const p = useGame.getState().presented!;
          scene.cards.sync({ ...p, actionDeckCount: Math.max(0, p.actionDeckCount - 1) });
        }
        await fly({ from, to: this.seatTarget(e.seat, e.deck === 'action' ? 'actions' : 'hand'), html: `<span class="mini-card back ${e.deck}"></span>`, className: 'token-card', duration: 0.45, speed });
        commit();
        if (!this.isMe(e.seat)) bump(`seat-${e.seat}`);
        return;
      }
      case 'CardDiscarded':
        commit();
        if (e.deck === 'action') scene?.cards.sync(useGame.getState().presented!);
        return;
      case 'SpellPurchased': {
        const from = scene ? scene.cardScreenPos({ kind: 'spell', slot: e.slot, card: e.card }) : this.center();
        if (scene) void scene.cards.take(e.slot, speed);
        audio.play('card');
        audio.play('coin', { gain: 0.6 });
        await fly({ from, to: this.seatTarget(e.seat, 'hand'), html: `<span class="mini-card spell s-${spell(e.def).school}"></span>`, className: 'token-card', duration: 0.55, speed });
        commit();
        if (!this.isMe(e.seat)) bump(`seat-${e.seat}`);
        return;
      }
      case 'ArcaniumRefilled': {
        audio.play('card', { gain: 0.5 });
        if (scene) await scene.cards.deal(e.slot, e.card, e.def, useGame.getState().presented!, speed * 1.2);
        commit();
        return;
      }
      case 'ArcaniumCleared': {
        if (scene) await scene.cards.clear(speed);
        commit();
        return;
      }
      case 'SpellSwapped': {
        commit();
        scene?.sync(useGame.getState().presented!);
        return;
      }
      case 'SpellCast': {
        const school = spell(e.def).school;
        await showcase({ kind: 'spell', def: e.def, stamp: 'APPROVED', color: PERMIT_HEX[COLOR_OF_SCHOOL[school]], speed, from: this.handPoint(e.seat, e.card), to: this.spellsPoint(e.seat), hold: this.readHold(e.seat) });
        commit();
        bump(this.isMe(e.seat) ? 'desk-spells' : `seat-${e.seat}-spells`);
        return;
      }
      case 'ActionCardPlayed': {
        // Memos Fly Free (P-17): a free memo takes wing instead of being filed at the Planning Office.
        if (e.free) audio.play('fold', { gain: 0.8 });
        await showcase({
          kind: 'action',
          def: e.def,
          stamp: e.free ? 'FREE MEMO' : 'FILED',
          color: e.free ? '#2f5a8a' : '#9a2f35',
          speed: speed * 1.3,
          small: true,
          from: this.handPoint(e.seat, e.card),
          to: scene ? scene.cardScreenPos({ kind: 'actionDiscard' }) : null,
          hold: this.readHold(e.seat),
        });
        commit();
        return;
      }
      case 'ProblemClaimed': {
        if (scene) void scene.burst('grand-auditor', '#e9d49a', speed);
        const at = this.problemPoint(e.problem);
        // The Problem lifts off the table, is stamped, and settles back…
        await showcase({ kind: 'problem', def: e.problem, stamp: 'RESOLVED', color: '#b08a2e', speed, from: at, to: at, hold: this.readHold(e.seat) });
        // …then the claimant's seal flies from their panel onto it.
        await fly({ from: this.playerPoint(e.seat), to: at, html: `<span class="seal-token" style="--seal:${SEAL_COLORS[e.seat]}"></span>`, className: 'token-seal', duration: 0.6, speed });
        audio.play('stamp', { gain: 0.6 });
        commit();
        scene?.sync(useGame.getState().presented!);
        return;
      }
      case 'LocationBlocked': {
        commit();
        if (scene) {
          scene.sync(useGame.getState().presented!);
          await scene.burst(e.location, '#ff4b40', speed);
        }
        return;
      }
      case 'PermitsExchanged': {
        const a = this.seatTarget(e.seat, 'permits', e.gave);
        const b = this.seatTarget(e.target, 'permits', e.took);
        await Promise.all([
          fly({ from: a, to: b, html: glyphHtml(e.gave), className: 'token-permit', speed }),
          fly({ from: b, to: a, html: glyphHtml(e.took), className: 'token-permit', speed }),
        ]);
        commit();
        return;
      }
      case 'DecreeEnacted': {
        audio.play('reveal', { gain: 0.7 });
        await showcase({ kind: 'decree', def: e.decree, stamp: 'ENACTED', color: '#2f7a5c', speed, from: this.playerPoint(e.seat), to: anchor('gazette', 'side-col'), hold: this.readHold(e.seat) });
        commit();
        // A toll Decree drops its barrier across the door it taxes.
        if (scene) void scene.decor.addToll(useGame.getState().presented!, e.decree, speed);
        return;
      }
      case 'DecreeIssued': {
        // The Ministry's own Decree: off the Decree deck on the table, into the Gazette.
        audio.play('round', { pitch: 0.9, gain: 0.7 });
        await banner('The Ministry issues a Decree', defName(e.decree), speed * 0.9, 'banner-round');
        const deck = scene ? scene.screenPos(scene.cards.anchor({ kind: 'decreeDeck' })) : undefined;
        audio.play('reveal', { gain: 0.7 });
        await showcase({ kind: 'decree', def: e.decree, stamp: 'ISSUED', color: '#8a6a2c', speed, ...(deck ? { from: deck } : {}), to: anchor('gazette', 'side-col'), hold: READ_HOLD });
        commit();
        if (scene) void scene.decor.addToll(useGame.getState().presented!, e.decree, speed);
        return;
      }
      case 'ArcaniumRefreshed': {
        // P-16: a visitor paid to clear the display; ArcaniumCleared and the refills follow.
        commit();
        float(this.playerPoint(e.seat), `Arcanium refreshed · −${e.mana} mana`, 'float-spend', speed);
        audio.play('shuffle', { gain: 0.7 });
        return;
      }
      case 'AssistantCapped': {
        commit();
        float(this.playerPoint(e.seat), `Assistant cap: ${e.cap}`, 'float-spend', speed);
        return;
      }
      case 'DecreeRepealed': {
        // Its toll barrier (if any) lifts and is carted off; Free Roads strikes it for everyone.
        if (scene) void scene.decor.removeToll(e.decree, speed);
        await showcase({
          kind: 'decree',
          def: e.decree,
          stamp: e.resolution ? 'STRUCK' : 'REPEALED',
          color: '#9a2f35',
          speed: speed * 1.2,
          small: true,
          from: anchor(`decree-${e.decree}`, 'gazette'),
          hold: e.resolution ? READ_HOLD : this.readHold(e.seat),
        });
        commit();
        return;
      }
      case 'DecreeFizzled': {
        // Free Roads: the card is cast, but its toll Decree can't come into force.
        await showcase({ kind: 'decree', def: e.decree, stamp: 'VOID', color: '#5a5a5a', speed: speed * 1.2, small: true, from: this.playerPoint(e.seat), hold: this.readHold(e.seat) });
        commit();
        float(this.playerPoint(e.seat), `${defName(e.resolution)} · no tolls`, 'float-ability', speed);
        return;
      }

      // ── Resolutions and Annex rooms (P-17, P-18; docs/09 §7.1) ──
      case 'ResolutionQueued': {
        // The claimed notice gets its red "effective next round" sash.
        commit();
        audio.play('stamp', { gain: 0.45, pitch: 1.2 });
        const at = this.problemPoint(e.problem);
        if (scene) await scene.cards.markQueued(e.problem, speed);
        float(at, `✦ ${defName(e.resolution)} · effective next round`, 'float-resolution', speed);
        return;
      }
      case 'ResolutionEnacted': {
        // Clock-out: the notice folds into a brass plaque that flies to the Resolutions rail, rests
        // there to be read, then the board changes.
        audio.play('gavel');
        if (scene) void scene.cards.markInForce(e.problem, speed);
        await showcase({ kind: 'resolution', def: e.resolution, stamp: 'RESOLVED', color: '#8a5a14', speed, from: this.problemPoint(e.problem), to: anchor('resolutions-rail', 'side-col'), hold: READ_HOLD });
        commit();
        audio.play('brass', { gain: 0.8 });
        bump(`resolution-${e.resolution}`, 'resolutions-rail');
        if (scene) await scene.decor.reveal(useGame.getState().presented!, e.resolution, props(), speed);
        if (e.resolution === 'resolution.early_closing') {
          audio.play('round', { pitch: 0.85 });
          await banner('Early Closing', `The Ministry now closes at ${closingHour(7)}`, RULE_BANNER, 'banner-round');
        }
        return;
      }
      case 'AnnexOpened': {
        // The room goes up in its bay: a construction, then its sign, then its lights (~2.6 s).
        commit();
        const p = useGame.getState().presented!;
        const pace = Math.min(speed, 1.25);
        const title = locationName(e.location);
        if (!scene) return;
        const cue = (c: 'land' | 'wall' | 'prop' | 'sign' | 'lights' | 'carry') => {
          if (c === 'land' || c === 'wall') audio.play('hammer', { pitch: c === 'wall' ? 0.8 : 1 + Math.random() * 0.2, gain: 0.8 });
          else if (c === 'prop') audio.play('place', { pitch: 1.2 + Math.random() * 0.3, gain: 0.5 });
          else if (c === 'sign' || c === 'carry') audio.play('creak');
          else if (c === 'lights') audio.play('hum');
        };
        await scene.lookAt(scene.bayAnchor(e.bay), async () => {
          await Promise.all([
            banner('The Annex opens', e.replaced ? `${title} · in place of ${shortLocation(e.replaced)}` : title, RULE_BANNER, 'banner-annex'),
            scene.openAnnex(p, e.location, e.bay, pace, cue),
          ]);
        });
        return;
      }
      case 'CoinFlipped': {
        // Magical Weather: the Ministry coin spins over the Department and lands.
        commit();
        audio.play('coinSpin', { gain: 0.8 });
        const heads = e.result === 'heads';
        const at = scene ? scene.screenPos(scene.roomAnchor(e.location)) : this.center();
        window.setTimeout(() => {
          audio.play(heads ? 'ting' : 'thud');
          float(at, heads ? 'Heads · again!' : 'Tails', heads ? 'float-heads' : 'float-tails', speed);
        }, 660 / speed);
        if (scene) await scene.flipCoin(e.location, e.result, speed);
        else await wait(0.7 / speed);
        return;
      }
      case 'HandsRevealed': {
        // The Fog Lifts: the mist over everyone's cards clears; hands turn face up.
        audio.play('wind');
        await fogClear(rectOf('.rivals-bar'), speed);
        commit();
        audio.play('card', { gain: 0.7 });
        window.setTimeout(() => audio.play('card', { gain: 0.5, pitch: 1.1 }), 120);
        await banner('The Fog Lifts', 'Every hand is face up', RULE_BANNER, 'banner-round');
        return;
      }
      case 'ActionCardGiven': {
        // The Mailroom: the card folds into a paper aeroplane and flies to the rival.
        audio.play('fold');
        const plane = `<svg class="paper-plane" viewBox="0 0 24 24" aria-hidden><path d="${ICON_PATHS.plane}" /></svg>`;
        await fly({ from: this.handPoint(e.seat, e.card), to: this.seatTarget(e.to, 'actions'), html: plane, className: 'token-plane', duration: 0.75, speed, arc: -150 });
        audio.play('whoosh', { gain: 0.5 });
        commit();
        bump(this.isMe(e.to) ? 'desk-actions' : `seat-${e.to}`);
        return;
      }
      case 'SpellRecovered': {
        // Lost Property: the spell comes back out of the discard.
        const from = scene ? scene.screenPos(scene.roomAnchor('lost-property')) : this.center();
        await showcase({ kind: 'spell', def: e.def, stamp: 'FOUND', color: '#6a4a9a', speed: speed * 1.1, small: true, from, to: this.handPoint(e.seat), hold: this.readHold(e.seat) });
        commit();
        if (!this.isMe(e.seat)) bump(`seat-${e.seat}`);
        return;
      }
      case 'SpellSuspended': {
        await showcase({ kind: 'spell', def: e.def, stamp: 'UNDER REVIEW', color: '#b5562a', speed: speed * 1.1, small: true, from: this.spellsPoint(e.owner), to: this.spellsPoint(e.owner), hold: this.readHold(e.seat) });
        commit();
        return;
      }
      case 'SpellRevoked': {
        await showcase({ kind: 'spell', def: e.def, stamp: 'REVOKED', color: '#9a2f35', speed, from: this.spellsPoint(e.owner), to: this.handPoint(e.owner), hold: this.readHold(e.seat) });
        commit();
        return;
      }
      case 'SpellReinstated': {
        commit();
        float(anchor(`seat-${e.owner}`) ?? this.center(), `✦ ${defName(e.def)} reinstated`, 'float-ability', speed);
        return;
      }
      case 'ManaPaid': {
        const from = this.seatTarget(e.seat, 'mana');
        const to = this.seatTarget(e.to, 'mana');
        await Promise.all(
          Array.from({ length: Math.min(e.amount, 4) }, (_, i) =>
            fly({ from, to, html: '<span class="glyph glyph-mana"></span>', className: 'token-mana', delay: i * 0.07, speed }),
          ),
        );
        audio.play('coin', { gain: 0.7 });
        commit();
        float(from, `−${e.amount}`, 'float-spend', speed);
        float(to, `+${e.amount}`, 'float-mana', speed);
        this.bumpSeat(e.to, 'mana');
        return;
      }
      case 'ActionStolen': {
        audio.play('card');
        await fly({ from: this.seatTarget(e.from, 'actions'), to: this.seatTarget(e.seat, 'actions'), html: '<span class="mini-card back action"></span>', className: 'token-card', duration: 0.5, speed });
        commit();
        return;
      }
      case 'TurnEnded': {
        // A clear full stop on the outgoing turn: a plate slides out from under their panel.
        commit();
        const me = this.isMe(e.seat);
        const name = useGame.getState().presented?.seats[e.seat]?.name ?? 'The player';
        await this.stacks; // "Spell Activated" cards still tucking in under the same panel
        await plate(
          me ? rectOf('.my-panel') : rectOf(`[data-anchor="seat-${e.seat}"]`),
          name,
          me ? 'End your turn' : 'Ends their turn',
          speed,
          me ? 'right' : 'below',
          SEAL_COLORS[e.seat],
        );
        return;
      }
      case 'TurnStarted': {
        commit();
        if (this.isMe(e.seat)) {
          audio.play('turn');
          // Morning Rush (P-17): the round's first placement resolves twice — say so when it's yours.
          const p = useGame.getState().presented;
          const rush = !!p && resolutionInForce(p, 'resolution.morning_rush') && !p.firstPlacementDone;
          await banner('Your turn', rush ? 'Morning Rush · your placement resolves twice' : 'Send an Assistant to a department', speed * 0.9, 'banner-turn');
        } else {
          // The handoff: the rival's card lifts and a slim banner in their colour names them.
          const p = useGame.getState().presented;
          const name = p?.seats[e.seat]?.name ?? 'Next player';
          audio.play('turn', { gain: 0.4, pitch: 0.8 });
          bump(`seat-${e.seat}`);
          await banner(name, 'takes the floor', speed * 0.8, 'banner-rival', SEAL_COLORS[e.seat]);
        }
        return;
      }
      case 'RoundStarted': {
        commit();
        scene?.setRound(e.round);
        audio.play('round');
        await banner(`Round ${e.round}`, roundHour(e.round), speed, 'banner-round');
        return;
      }
      case 'AssistantsRetrieved': {
        commit();
        audio.play('hop', { gain: 0.8 });
        if (scene) await scene.retrieveAll(useGame.getState().presented!, speed);
        return;
      }
      case 'InternsHired': {
        // P-12: the Afternoon Shift. Each Intern then appears on its bench (AssistantGained); any
        // brought forward by Labour Accord leave the entrance to take their places.
        if (scene) await scene.decor.dismissInterns(speed);
        commit();
        audio.play('round', { pitch: 1.12 });
        await banner('The Afternoon Shift', 'Every clerk takes on an Intern', speed, 'banner-round');
        return;
      }
      case 'AssistantGained':
      case 'AssistantRemoved':
        commit();
        if (scene) scene.sync(useGame.getState().presented!);
        if (e.t === 'AssistantGained' && e.intern) {
          bump(this.isMe(e.seat) ? 'desk-assistants' : `seat-${e.seat}`, `seat-${e.seat}`);
          audio.play('place', { pitch: 1.3, gain: 0.6 });
          await wait(0.18 / speed);
        }
        return;
      case 'StampTaken': {
        // P-11: the Stamp of Precedence leaves its holder's bench for the taker's.
        commit();
        float(this.scene ? this.scene.screenPos(this.scene.roomAnchor('precedence')) : this.playerPoint(e.seat), '★ Stamp of Precedence', 'float-ability', speed);
        bump(this.isMe(e.seat) ? 'desk-identity' : `seat-${e.seat}`);
        audio.play('hop', { pitch: 0.8 });
        if (scene) await scene.moveStamp(e.seat, speed);
        audio.play('place', { pitch: 0.6, gain: 1 });
        return;
      }
      case 'AssistantSentHome':
        commit();
        return;
      case 'StartingPackage': {
        // P-15: the Bureaucromancer's starting package (its gains follow as their own beats).
        commit();
        const pkg = C.identities.get(e.identity)?.package;
        if (pkg) float(this.playerPoint(e.seat), '✦ Starting package', 'float-ability', speed);
        bump(this.isMe(e.seat) ? 'desk-identity' : `seat-${e.seat}`);
        audio.play('reveal', { gain: 0.5 });
        await wait(0.25 / speed);
        return;
      }
      case 'DraftResolved':
        commit();
        await banner('Appointments confirmed', 'The Ministry opens its doors', speed, 'banner-round');
        if (this.dealt) await this.setupSequence(speed);
        return;
      case 'AbilityTriggered': {
        commit();
        if (this.shownSpells.has(e)) return; // its "Spell Activated" card has just been shown
        float(this.sourcePoint(e.source, e.seat), `✦ ${defName(e.source.kind === 'card' || e.source.kind === 'identity' ? e.source.def : null)}`, 'float-ability', speed);
        return;
      }
      case 'GameEnded': {
        commit();
        const p = useGame.getState().presented;
        scene?.setRound(p ? lastRound(p) : 8, true);
        audio.play('round', { pitch: 0.75 });
        await banner('Closing time', p && lastRound(p) < p.options.rounds ? `${closingHour(lastRound(p))} · the Grand Auditor tallies the Ledger` : 'The Grand Auditor tallies the Ledger', speed * 0.8, 'banner-round');
        return;
      }
      default:
        commit();
        return;
    }
  }

  // ─── Opening ───────────────────────────────────────────────────────────────

  /** Present one synthetic beat (the opening deal replays what the engine dealt before the draft). */
  private beat(e: GameEvent, speed: number): Promise<void> {
    return this.play({ seq: -1, e } as LoggedEvent, speed);
  }

  /**
   * The opening deal, after the draft: the decks are shuffled, the City Problems laid out, the
   * Arcanium stocked, then everyone is dealt their hand one card at a time, round the table.
   */
  private async setupSequence(speed: number): Promise<void> {
    const dealt = this.dealt;
    this.dealt = null;
    if (!dealt) return;
    const scene = this.scene;
    const me = useGame.getState().you?.seat;
    const update = (fn: (p: PlayerView) => void) => {
      const p = structuredClone(useGame.getState().presented!);
      fn(p);
      useGame.setState({ presented: p });
      scene?.cards.sync(p);
    };

    // 1 · Shuffle every deck.
    scene?.cards.setProblemLayout(dealt.problems.length);
    update(() => undefined);
    audio.play('shuffle');
    window.setTimeout(() => audio.play('shuffle', { gain: 0.8 }), 950 / speed);
    await scene?.cards.shuffle(speed);

    // 2 · City Problems come out of the incident file.
    const flights: Promise<void>[] = [];
    for (const slot of dealt.problems) {
      update((p) => p.problems.push({ ...slot, claims: [] }));
      audio.play('card', { gain: 0.7 });
      flights.push(scene ? scene.cards.dealProblem(slot.problem, speed * 1.1) : Promise.resolve());
      await wait(0.16 / speed);
    }
    await Promise.all(flights);
    scene?.cards.setProblemLayout(null);

    // 3 · The Arcanium is stocked.
    const deals: Promise<void>[] = [];
    for (const [slot, card] of dealt.arcanium.entries()) {
      if (!card) continue;
      deals.push(this.beat({ t: 'ArcaniumRefilled', slot, card, def: dealt.cards[card]! }, speed * 1.1));
      await wait(0.14 / speed);
    }
    await Promise.all(deals);
    await wait(0.2 / speed);

    // 4 · Hands, dealt round the table from the first player: spells, then Action cards.
    const order = Array.from({ length: dealt.playerCount }, (_, k) => (dealt.firstSeat + k) % dealt.playerCount);
    for (const deck of ['spell', 'action'] as const) {
      const passes = Math.max(...dealt.seats.map((s) => (deck === 'spell' ? s.spellHandCount : s.actionHandCount)));
      for (let k = 0; k < passes; k++) {
        await Promise.all(
          order.map((seat, i) => {
            const st = dealt.seats[seat]!;
            if (k >= (deck === 'spell' ? st.spellHandCount : st.actionHandCount)) return Promise.resolve();
            const own = seat === me ? (deck === 'spell' ? st.spellHand : st.actionHand)?.[k] : undefined;
            const e: GameEvent = own
              ? { t: 'CardDrawn', seat, card: own, def: dealt.cards[own]!, deck }
              : { t: 'CardDrawn', seat, card: `deal:${seat}:${deck}:${k}`, deck };
            return wait((i * 0.07) / speed).then(() => this.beat(e, speed * 1.15));
          }),
        );
        scene?.cards.sync(useGame.getState().presented!);
      }
    }
    await wait(0.35 / speed);
  }

}

export const director = new Director();

// Debug handle for development builds (replay beats from the console / automated visual checks).
if (import.meta.env.DEV) (globalThis as unknown as { __bcmDirector: Director }).__bcmDirector = director;
